import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { X, Tag, Sparkles } from 'lucide-react';

const PropertiesModal = ({ open, onClose, properties = [], dealTitle = '' }) => {
  if (!properties || properties.length === 0) return null;

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '24px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              background: 'linear-gradient(to bottom, #ffffff, #f8fafc)',
            },
          }}
        >
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col h-full"
              style={{ minHeight: 0 }}
            >
              {/* Header */}
              <div className="relative p-6 bg-gradient-to-r from-[#FE696F] to-[#ff5555] flex-shrink-0">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Tag className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Deal Properties</h2>
                      {dealTitle && (
                        <p className="text-sm text-white/90 mt-1">{dealTitle}</p>
                      )}
                    </div>
                  </div>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <X className="w-5 h-5" />
                  </IconButton>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: 0 }}>
                <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
                  <Sparkles className="w-4 h-4 text-[#FE696F]" />
                  <span>{properties.length} {properties.length === 1 ? 'Property' : 'Properties'}</span>
                </div>

                {/* Properties Grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {properties.map((property, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group relative overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      {/* Gradient accent */}
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#FE696F] to-[#ff5555] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      <div className="p-5">
                        {/* Horizontal Layout */}
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(254, 105, 111, 0.1)' }}>
                                <Tag className="w-3.5 h-3.5" style={{ color: '#FE696F' }} />
                              </div>
                              <h3 className="text-sm font-semibold text-gray-700 truncate">
                                {property.name}
                              </h3>
                            </div>
                            <div className="ml-8">
                              <p className="text-base font-medium text-gray-900 break-words">
                                {property.value}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hover effect overlay */}
                      <div className="absolute inset-0 transition-all duration-300 pointer-events-none" style={{ background: 'linear-gradient(to bottom right, rgba(254, 105, 111, 0), rgba(254, 105, 111, 0))' }}></div>
                      <div className="absolute inset-0 group-hover:bg-gradient-to-br group-hover:from-[rgba(254,105,111,0.05)] group-hover:to-[rgba(254,105,111,0.05)] transition-all duration-300 pointer-events-none"></div>
                    </motion.div>
                  ))}
                </div>

                {/* Empty state (shouldn't show, but just in case) */}
                {properties.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="p-4 bg-gray-100 rounded-full mb-4">
                      <Tag className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No properties available</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex-shrink-0">
                <div className="flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="px-6 py-2.5 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
                    style={{ 
                      background: 'linear-gradient(to right, #FE696F, #ff5555)',
                    }}
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default PropertiesModal;
